import React from 'react';
import { useSelector } from 'react-redux';
import { getResources } from 'react-admin';
import { NavLink, matchPath } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Toolbar from '@material-ui/core/Toolbar';
import { default as MuiAppBar } from '@material-ui/core/AppBar';
import { createStyles, withStyles } from '@material-ui/styles';
import { groupBy } from 'lodash';
import UserMenu from './UserMenu';
import AppBarTab from './AppBarTab';
import logo from 'rollo.png';
import { useRewardsPendingCount } from 'contexts/rewardsPendingCount';

const styles = (theme) =>
  createStyles({
    root: {
      position: 'relative',
      minWidth: '1415px',
    },

    toolbar: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      boxShadow: theme.shadows[1],
    },

    logo: {
      width: theme.spacing(9),
      marginBottom: 8,
      marginRight: theme.spacing(12),
      '@media (max-width: 1500px)': {
        marginRight: theme.spacing(4),
      },
    },

    tabs: {
      flex: 'auto',
      overflow: 'visible',
    },

    grow: {
      flexGrow: 1,
      '@media (max-width: 1350px)': {
        flexGrow: 0,
      },
    },

    buttons: {
      display: 'flex',
      alignItems: 'center',

      '& > *': {
        marginRight: theme.spacing(3.5),
      },
    },

    actionTab: {
      '&:not(:last-child)': {
        marginRight: theme.spacing(4),
      },
    },
    complexLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    },
    pendingBadge: {
      minWidth: theme.spacing(4),
      padding: '4px',
      borderRadius: theme.spacing(2),
      backgroundColor: 'rgba(99, 84, 245, 0.12)',
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: 14,
      lineHeight: 1.2,
      textAlign: 'center',
    },
    tab: {
      '&.MuiTab-root': {
        '@media (max-width: 1500px)': {
          marginRight: theme.spacing(2),
        },
      },
    },
  });

const complexMenus = [{ label: 'Rewards', value: 'rewards' }];
const complexMenuValues = complexMenus.map((menu) => menu.value);

const NonTab = ({ className, children }: { className?: string; children?: React.ReactNode }) => (
  <Box className={className}>{children}</Box>
);
const AppBar = ({ classes }) => {
  const { pendingCount } = useRewardsPendingCount();
  const getIndex = (items, menus) => {
    const found = items.find((item) => matchPath(window.location.pathname, item));
    if (!found) {
      return false;
    }

    return complexMenuValues.includes(found.menu) ? found.menu : found.index;
  };

  const allItems = useSelector((state) => getResources(state))
    .filter((resource) => resource.options.menu)
    .map((resource) => {
      const { label, menu, order = 100 } = resource.options;
      return {
        index: resource.name,
        path: `/${resource.name}`,
        label,
        menu,
        order,
      };
    })
    .sort((a, b) => a.order - b.order);

  const menus = groupBy(allItems, 'menu');

  let menuIndex = getIndex(allItems, menus);

  const renderComplexLabel = (menu) => {
    if (menu.value !== 'rewards') return menu.label;
    const showBadge = pendingCount !== null;

    return (
      <Box className={classes.complexLabel}>
        {menu.label}
        {showBadge ? <Box className={classes.pendingBadge}>{pendingCount}</Box> : null}
      </Box>
    );
  };

  return (
    <MuiAppBar className={classes.root} color="secondary" position="static" elevation={0}>
      <Toolbar className={classes.toolbar}>
        <img className={classes.logo} src={logo} alt="Rollo Logo" />
        <Tabs
          className={classes.tabs}
          value={menuIndex}
          indicatorColor="primary"
          textColor="primary">
          {menus.primary &&
            menus.primary.map((item) => (
              <AppBarTab
                component={NavLink}
                key={item.path}
                value={item.index}
                to={item.path}
                label={item.label}
                className={classes.tab}
              />
            ))}
          {complexMenus.map((item) => (
            <AppBarTab
              className={classes.tab}
              key={item.value}
              label={renderComplexLabel(item)}
              value={item.value}
              submenu={menus[item.value]}
              selected={menuIndex === item.value}
            />
          ))}
          <NonTab className={classes.grow} />
          {menus.actions &&
            menus.actions.map((item) => (
              <AppBarTab
                className={classes.actionTab}
                component={NavLink}
                key={item.path}
                value={item.index}
                to={item.path}
                label={item.label}
              />
            ))}
          <UserMenu />
        </Tabs>
      </Toolbar>
    </MuiAppBar>
  );
};

export default withStyles(styles)(AppBar);
